import React, { useState, useEffect, useRef, useCallback } from 'react'
import '../../styles/CreateTicketPage.css'
import {
  LANGUAGES, VEHICLE_AGES, PRIORITIES, CHANNELS, SOURCE_CHANNELS,
  CUSTOMER_TYPES, TICKET_TYPES, LOCATION_TYPES,
} from '../../constants/ticketFormOptions'

function VehicleFieldDropdown({ fetchJson, apiUrl, value, placeholder, onSelect, onClear }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const dropRef = useRef(null)
  const listRef = useRef(null)
  const loadingRef = useRef(false)
  const debounceRef = useRef(null)

  const load = useCallback(async (q, pg, append) => {
    if (!fetchJson || loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(pg) })
      if (q) params.set('q', q)
      const data = await fetchJson(`${apiUrl}?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setList(prev => append ? [...prev, ...results] : results)
      setHasMore(!!data?.next)
      setPage(pg)
    } catch { } finally { loadingRef.current = false; setLoading(false) }
  }, [fetchJson, apiUrl])

  useEffect(() => {
    if (!open) return
    clearTimeout(debounceRef.current)
    if (search.length === 1) return
    debounceRef.current = setTimeout(() => load(search, 1, false), search.length >= 2 ? 300 : 0)
    return () => clearTimeout(debounceRef.current)
  }, [open, search, load])

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasMore || loading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) load(search, page + 1, true)
  }

  return (
    <div ref={dropRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder={placeholder}
          value={open ? search : (value || '')}
          onFocus={() => { setOpen(true); setSearch('') }}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
          style={{ paddingRight: value ? '28px' : undefined }}
        />
        {value && !open && (
          <button
            type="button"
            onClick={onClear}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 0 }}
          >×</button>
        )}
      </div>
      {open && (
        <div
          ref={listRef}
          onScroll={handleScroll}
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4 }}
        >
          <div
            onClick={() => { onClear(); setOpen(false) }}
            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          >{placeholder}</div>
          {list.map(item => (
            <div
              key={item.id}
              onClick={() => { onSelect(item.name); setOpen(false); setSearch('') }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9', background: value === item.name ? '#EFF6FF' : undefined }}
              onMouseEnter={e => e.currentTarget.style.background = value === item.name ? '#DBEAFE' : '#F8FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = value === item.name ? '#EFF6FF' : '#fff'}
            >
              <div style={{ fontSize: 13, color: '#111827' }}>{item.name}</div>
            </div>
          ))}
          {search.length === 1 && <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Type at least 2 characters to search…</div>}
          {loading && <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>}
          {!loading && search.length !== 1 && list.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No results found</div>}
          {!loading && hasMore && <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>}
        </div>
      )}
    </div>
  )
}

// Dropdown of alternate swap attempts for CNC Error Code / CNC Transaction ID —
// each entry carries both fields together, so selecting one from either
// dropdown applies both (see applySwapEntry in CreateTicketPage).
function SwapAttemptDropdown({ entries, primary, onSelect }) {
  if (!entries.length) return null
  return (
    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4 }}>
      <div style={{ padding: '6px 12px', fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.4 }}>OTHER SWAP ATTEMPTS</div>
      {entries.map((entry, i) => (
        <div
          key={entry.id || i}
          onClick={() => onSelect(entry)}
          style={{ padding: '8px 12px', cursor: 'pointer', borderTop: '1px solid #F1F5F9' }}
          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
            {primary === 'transaction_id' ? entry.transaction_id : entry.error_code}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
            {primary === 'transaction_id' ? entry.error_code : `TXN ${entry.transaction_id}`}
            {entry.swap_start ? ` · ${new Date(entry.swap_start).toLocaleTimeString()}` : ''}
          </div>
        </div>
      ))}
    </div>
  )
}

// The Type of Vehicle field is a fixed <select> (E-Auto/E-Loader/E-Rickshaw/
// 2 Wheeler/Other) — vehicle data from other APIs (search, swap-details) uses
// its own vocabulary (e.g. "eBike") that doesn't match those option values
// verbatim, so the select can't display it. Map to the closest known option,
// case-insensitively, defaulting to "2 Wheeler" for anything unrecognized.
const VEHICLE_TYPE_OPTIONS = ['E-Auto', 'E-Loader', 'E-Rickshaw', '2 Wheeler', 'Other']
const normalizeVehicleType = (val) => {
  if (!val) return ''
  const match = VEHICLE_TYPE_OPTIONS.find(o => o.toLowerCase() === String(val).trim().toLowerCase())
  return match || '2 Wheeler'
}

// swap-details/search APIs return the account as a display label (e.g.
// "Fleet"), but the Customer Type select's option values are lowercase
// (e.g. "fleet") — map to the matching option value so the select prefills.
const normalizeCustomerType = (val) => {
  if (!val) return ''
  const needle = String(val).trim().toLowerCase()
  const match = CUSTOMER_TYPES.find(o => o.value.toLowerCase() === needle || o.label.toLowerCase() === needle)
  return match ? match.value : ''
}

// Merges a swap-details API response into the ticket form.
//   fillEmptyOnly=true  -> only fills fields that are currently blank (used
//                          automatically once VIN hits 17 chars — never
//                          clobbers data the search API or agent already set)
//   fillEmptyOnly=false -> overwrites everything found (used by the manual
//                          "Fetch from swap details" fallback, where there's
//                          nothing worth preserving yet)
// Returns { hasVehicleData, hasSwapData } so callers know if anything was found.
function buildSwapDetailsApplier({ setForm, setSwapDetailsList, setSwapDetailsRaw, setPendingSwapStation }) {
  return (data, { fillEmptyOnly }) => {
    const vd = data?.vehicle_details
    const hasVehicleData = !!vd && Object.values(vd).some(v => v !== null && v !== undefined && v !== '')
    const list = Array.isArray(data?.swap_details)
      ? data.swap_details
      : (data?.swap_details ? [data.swap_details] : [])
    setSwapDetailsList(list)
    // Keep the untouched swap_details from the API so it can be sent as-is in the create-ticket payload
    setSwapDetailsRaw(data?.swap_details ?? null)
    const first = list[0]

    setForm(f => {
      const next = { ...f }
      const setIfNeeded = (key, val) => {
        if (val === undefined || val === null || val === '') return
        if (fillEmptyOnly && next[key]) return
        next[key] = val
      }
      if (vd) {
        setIfNeeded('vin', vd.vin_number || vd.vin)
        setIfNeeded('registrationNumber', vd.registration_number)
        setIfNeeded('vehicleId', vd.id ? String(vd.id) : '')
        setIfNeeded('batteryId', vd.battery_id)
        if (vd.type_of_vehicle) setIfNeeded('vehicleType', normalizeVehicleType(vd.type_of_vehicle))
        setIfNeeded('oemBrandName', vd.oem)
        setIfNeeded('serviceProvider', vd.service_provider)
        setIfNeeded('customerType', normalizeCustomerType(vd.account))
        setIfNeeded('experience', vd.vehicle_age)
      }
      // CNC Error Code / Transaction ID priority:
      //   1. first swap_details record (latest swap) — always wins, even in
      //      fillEmptyOnly mode, since the vehicle search may already have
      //      filled these from the older vehicle_details values.
      //   2. vehicle_details.cnc_error_code / cnc_transaction_id — only when
      //      there is no swap record at all.
      const isBlank = (v) => v === undefined || v === null || v === ''
      if (first) {
        next.cncErrorCode = isBlank(first.error_code) ? '' : String(first.error_code)
        next.cncTransactionId = isBlank(first.transaction_id) ? '' : String(first.transaction_id)
      } else if (vd) {
        setIfNeeded('cncErrorCode', vd.cnc_error_code || vd.last_cnc_error_code)
        setIfNeeded('cncTransactionId', vd.cnc_transaction_id || vd.last_cnc_id)
      }
      return next
    })

    const station = first?.station
    if (station && typeof station === 'object' && station.id) {
      setForm(f => {
        if (fillEmptyOnly && f.stationId) return f
        return {
          ...f,
          regionId: station.region?.id || f.regionId,
          regionSlug: station.region?.slug || f.regionSlug,
          city: station.city?.id || f.city,
          citySlug: station.city?.slug || f.citySlug,
          stationId: station.id,
          stationSlug: station.slug || '',
          // Mirrors manual station selection: station code -> Station ID field,
          // station name -> Incident Area.
          area: station.name || f.area,
          ...(f.subCategorySlug !== 'battery-stuck-at-vehicle' ? { nearbyStationId: station.station_code || f.nearbyStationId } : {}),
        }
      })
      setPendingSwapStation({ code: station.station_code || '', name: station.name || station.slug || station.id })
    } else if (typeof station === 'string' && station) {
      setPendingSwapStation({ code: station, name: '' })
    } else if (station && typeof station === 'object') {
      const code = station.station_code || station.code || ''
      const name = station.name || ''
      if (code || name) setPendingSwapStation({ code, name })
    }

    return { hasVehicleData, hasSwapData: list.length > 0 }
  }
}

// Normalize /api/vehicles/search-bar response to always be an array of
// { customer_details, vehicle_details } objects
const normalizeVehicleResults = (data) => {
  if (!data) return []
  const hasContent = r => {
    const vd = r?.vehicle_details
    const cd = r?.customer_details
    return (vd && (vd.vin_number || vd.vin || vd.registration_number || vd.id)) ||
      (cd && (cd.name || cd.phone))
  }
  if (hasContent(data)) return [data]
  if (Array.isArray(data)) return data.filter(hasContent)
  if (data.results) {
    const r = data.results
    if (Array.isArray(r)) return r.filter(hasContent)
    if (hasContent(r)) return [r]
  }
  // Fuzzy/partial queries return { vehicle_details: [...] } — a flat array of
  // vehicle records (not vehicle+customer pairs) — one per matching vehicle.
  if (Array.isArray(data.vehicle_details)) {
    return data.vehicle_details
      .filter(vd => vd && (vd.vin_number || vd.vin || vd.registration_number || vd.id))
      .map(vd => ({ vehicle_details: vd, customer_details: data.customer_details || null }))
  }
  return []
}

const emptyForm = () => ({
  // Vehicle
  vin: '', registrationNumber: '', vehicleId: '',
  batteryId: '', qisId: '', vehicleType: '', oemBrandName: '',
  serviceProvider: '', cncErrorCode: '', cncTransactionId: '',
  // Customer
  riderName: '', phone: '', email: '',
  preferredChannel: '', customerType: '', channel: '',
  language: 'Hindi', experience: '',
  // Issue
  categoryName: '', subCategoryName: '', categoryId: '', subCategoryId: '',
  categorySlug: '', subCategorySlug: '',
  region: '', coordinatorId: '', coordinatorName: '',
  ticketType: 'call_center', priority: 'urgent', previousTicketId: '', disposition: '',
  locationType: '',
  stationId: '', stationSlug: '',           // stationId=UUID for payload, stationSlug for dependent lookups
  nearbyStationId: '', dockId: '',
  state: '', stateId: '',                   // stateId=UUID for payload
  city: '', citySlug: '',                   // city=UUID for payload, citySlug for stations API
  regionId: '', regionSlug: '',             // regionId=UUID for coordinator API, regionSlug if needed
  area: '', description: '',
})

const CreateTicketPage = ({ fetchJson, setCurrentPage, setSelectedVehicle, setSelectedTicket, callLogId: callLogIdProp = '', fromCallProp = false, editMode = false, editTicketId = '', onEditSuccess = null }) => {
  const [form, setForm] = useState(emptyForm())
  const [draftModalOpen, setDraftModalOpen] = useState(false)
  const draftSaveTimer = useRef(null)
  const latestDraftRef = useRef({ form: emptyForm() })
  const ticketCreatedRef = useRef(false)
  const [submitting, setSubmitting] = useState(false)
  const [resolvingNow, setResolvingNow] = useState(false)
  const editInitialFormRef = useRef(null)
  const [createdTicket, setCreatedTicket] = useState(null)
  const [whatsappHint, setWhatsappHint] = useState('')

  /* ── toasts ── */
  const [toasts, setToasts] = useState([])
  const toastTimers = useRef({})
  useEffect(() => () => { Object.values(toastTimers.current).forEach(clearTimeout) }, [])

  const dismissToast = (id) => {
    clearTimeout(toastTimers.current[id])
    delete toastTimers.current[id]
    setToasts(t => t.filter(x => x.id !== id))
  }

  const showToast = (ok, msg, withViewTickets = false, title = null) => {
    const id = Date.now()
    setToasts(t => [...t, { id, ok, msg, withViewTickets, title }])
    toastTimers.current[id] = setTimeout(() => dismissToast(id), 5000)
  }

  /* prop takes priority; fall back to localStorage for the new-tab auto-open scenario */
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const callLogId = callLogIdProp || (typeof window !== 'undefined' ? localStorage.getItem('ozonetel_call_log_id') || '' : '')
  const fromCall = fromCallProp || urlParams.get('source') === 'call'   // true when auto-opened by busyAgent
  const queryMode = urlParams.get('mode') === 'query'                   // true when opened as a lightweight "Query" ticket

  /* guard so the auto-search only fires once on mount */
  const autoSearchedRef = useRef(false)

  const DRAFT_KEY = 'crm_ticket_draft'

  /* ── pre-fill form when in edit mode ── */
  useEffect(() => {
    if (!editMode || !editTicketId || !fetchJson) return
    fetchJson(`/api/tickets/ticket/get-form-data/${editTicketId}`)
      .then(data => {
        const cd = data?.customer_details || {}
        const vd = data?.vehicle_details || {}
        const id = data?.issue_details || {}
        if (id.created_from?.toLowerCase() === 'whatsapp' && id.whatsapp_category) {
          setWhatsappHint(id.whatsapp_category)
        }
        const prefilled = {
          // vehicle
          vin: vd.vin_number || '',
          registrationNumber: vd.registration_number || '',
          batteryId: vd.battery_id || '',
          qisId: vd.qis_id || '',
          vehicleType: normalizeVehicleType(vd.type_of_vehicle),
          oemBrandName: vd.oem || '',
          serviceProvider: vd.service_provider || '',
          cncErrorCode: vd.cnc_error_code || '',
          cncTransactionId: vd.cnc_transaction_id || '',
          // customer
          phone: cd.phone || '',
          riderName: cd.name || '',
          email: cd.email || '',
          preferredChannel: cd.preferred_channel || '',
          customerType: cd.customer_type || '',
          channel: cd.source_channel || '',
          language: cd.language || '',
          experience: cd.vehicle_age || '',
          // issue
          ticketType: id.ticket_type || 'call_center',
          priority: id.priority || 'urgent',
          locationType: id.location_type || '',
          area: id.incident_area || '',
          description: id.descripition || '',
          categorySlug: id.category || '',
          subCategorySlug: id.sub_category || '',
          stationId: id.station || '',
          stateId: id.incident_state || '',
          regionId: id.region || '',
          regionSlug: id.region_slug || '',
          city: id.incident_city || '',
          citySlug: id.city_slug || '',
          dockId: id.dock_id || '',
          nearbyStationId: id.nearby_station || '',
          previousTicketId: id.previous_ticket || '',
          disposition: id.disposition || '',
        }
        editInitialFormRef.current = prefilled
        setForm(f => ({ ...f, ...prefilled }))
        if (id.category) {
          loadSubCategories(id.category, 1, false)
        }
      })
      .catch(() => { })
  }, [editMode, editTicketId, fetchJson])

  /* ── check for existing draft on mount ── */
  useEffect(() => {
    if (fromCall || editMode) return
    try {
      const raw = localStorage.getItem(DRAFT_KEY)
      if (raw) {
        const draft = JSON.parse(raw)
        if (draft?.form && Object.values(draft.form).some(v => v !== '' && v !== null && v !== undefined)) {
          setDraftModalOpen(true)
        }
      }
    } catch { }
  }, [])

  /* ── keep ref in sync so unmount handler always has latest values ── */
  useEffect(() => {
    latestDraftRef.current = { form, fromCall }
  })

  /* ── save draft to localStorage on form change (debounced) ── */
  useEffect(() => {
    if (fromCall || editMode) return
    clearTimeout(draftSaveTimer.current)
    draftSaveTimer.current = setTimeout(() => {
      const hasData = form.vin || form.riderName || form.phone || form.categorySlug || form.description
      if (hasData) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form }))
      }
    }, 500)
    return () => clearTimeout(draftSaveTimer.current)
  }, [form, fromCall])

  /* ── save draft immediately on unmount (catches quick navigations) ── */
  useEffect(() => {
    return () => {
      const { form: f, fromCall: fc } = latestDraftRef.current
      if (fc) return
      const hasData = f.vin || f.riderName || f.phone || f.categorySlug || f.description
      if (hasData) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: f }))
      }
    }
  }, [])

  const handleRestoreDraft = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}')
      if (draft?.form) {
        setForm(draft.form)
      }
    } catch { }
    setDraftModalOpen(false)
  }

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY)
    setDraftModalOpen(false)
    handleReset()
  }

  /* ── browser tab close / refresh guard ── */
  useEffect(() => {
    if (!fromCall) return
    const handleBeforeUnload = (e) => {
      if (ticketCreatedRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [fromCall])

  /* ── categories ── */
  const [catList, setCatList] = useState([])
  const [catsLoading, setCatsLoading] = useState(false)
  const [catsError, setCatsError] = useState(null)
  const [catPage, setCatPage] = useState(1)
  const [catHasMore, setCatHasMore] = useState(false)
  const [catOpen, setCatOpen] = useState(false)
  const [catSelectedName, setCatSelectedName] = useState('')
  const catDropRef = useRef(null)
  const catListRef = useRef(null)
  const catLoadingRef = useRef(false)
  const [subcatList, setSubcatList] = useState([])
  const [subcatsLoading, setSubcatsLoading] = useState(false)
  const [subcatPage, setSubcatPage] = useState(1)
  const [subcatHasMore, setSubcatHasMore] = useState(false)
  const [subcatOpen, setSubcatOpen] = useState(false)
  const [subcatSelectedName, setSubcatSelectedName] = useState('')
  const subcatDropRef = useRef(null)
  const subcatListRef = useRef(null)
  const subcatLoadingRef = useRef(false)

  /* ── vehicle lookup (auto mode) ── */
  const [vSearch, setVSearch] = useState('')
  const [vResults, setVResults] = useState([])
  const [vSearching, setVSearching] = useState(false)
  const [vLastSearch, setVLastSearch] = useState('')
  const [vehicleFound, setVehicleFound] = useState(null)
  const [showAllTickets, setShowAllTickets] = useState(false)
  const vDebounce = useRef(null)
  const vDropRef = useRef(null)

  /* ── vehicle suggestions for the rider's phone (inbound call) — lets the agent
     skip manual VIN search and jump straight to filling the form ── */
  const [vinSuggestions, setVinSuggestions] = useState([])
  const [vinSuggestionsLoading, setVinSuggestionsLoading] = useState(false)
  const [selectingSuggestionId, setSelectingSuggestionId] = useState(null)
  const [selectedVinSuggestionId, setSelectedVinSuggestionId] = useState(null)
  const vinSuggestionsFetchedRef = useRef(false)

  /* ── swap-details lookup — prefills whatever the search API didn't already
     fill (vehicle info, CNC Error Code / CNC Transaction ID / Region / City /
     Station ID) once a full 17-char VIN is entered (typed, searched, or
     autofilled from a suggestion). Takes 5-6s, so callers show a loader. ── */
  const [swapDetailsLoading, setSwapDetailsLoading] = useState(false)
  const [pendingSwapStation, setPendingSwapStation] = useState(null) // { code, name } — set aside until stationsList has data to match against
  const [swapDetailsList, setSwapDetailsList] = useState([]) // all swap attempts for the VIN — [0] prefills, the rest are picked from a dropdown
  const [swapDetailsRaw, setSwapDetailsRaw] = useState(null) // raw swap_details from the swap-details API, sent unchanged in the create-ticket payload
  const [cncErrorDropdownOpen, setCncErrorDropdownOpen] = useState(false)
  const [cncTxnDropdownOpen, setCncTxnDropdownOpen] = useState(false)
  const cncErrorDropRef = useRef(null)
  const cncTxnDropRef = useRef(null)
  const swapDetailsVinRef = useRef('')
  const swapDetailsDebounce = useRef(null)
  // Loading state for the manual "Fetch from Swap Details" fallback button
  // shown when the VIN search returns no results.
  const [noResultSwapLoading, setNoResultSwapLoading] = useState(false)
  // Shared merge helper — see buildSwapDetailsApplier above. fillEmptyOnly
  // controls whether it only fills blank fields or overwrites everything.
  const applySwapDetails = useCallback(
    buildSwapDetailsApplier({ setForm, setSwapDetailsList, setSwapDetailsRaw, setPendingSwapStation }),
    []
  )

  /* ── customer search ── */
  const [cSearch, setCSearch] = useState('')
  const [cResults, setCResults] = useState([])
  const [cSearching, setCSearching] = useState(false)
  const [cLastSearch, setCLastSearch] = useState('')
  const cDebounce = useRef(null)
  const cDropRef = useRef(null)

  const loadCategories = useCallback(async (page = 1, append = false) => {
    if (!fetchJson || catLoadingRef.current) return
    catLoadingRef.current = true
    setCatsLoading(true)
    if (!append) setCatsError(null)
    try {
      const params = new URLSearchParams({ page: String(page) })
      if (queryMode) params.set('category_type', 'query')
      const data = await fetchJson(`/api/categories/category?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setCatList(prev => append ? [...prev, ...results] : results)
      setCatHasMore(!!data?.next)
      setCatPage(page)
    } catch (err) {
      setCatsError(err?.message || 'Failed to load categories')
    } finally {
      catLoadingRef.current = false
      setCatsLoading(false)
    }
  }, [fetchJson, queryMode])

  useEffect(() => { loadCategories(1, false) }, [loadCategories])

  /* ── sync display names when lists load in edit mode ── */
  useEffect(() => {
    if (!editMode || !form.categorySlug || catList.length === 0) return
    const cat = catList.find(c => (c.slug || c.name) === form.categorySlug)
    if (!cat) return
    setCatSelectedName(cat.name || form.categorySlug)
    setForm(f => ({ ...f, categoryName: cat.name || f.categorySlug, categoryId: String(cat.id || '') }))
  }, [catList, form.categorySlug, editMode])

  useEffect(() => {
    if (!editMode || !form.subCategorySlug || subcatList.length === 0) return
    const sub = subcatList.find(s => (s.slug || s.name) === form.subCategorySlug)
    if (!sub) return
    setSubcatSelectedName(sub.name || form.subCategorySlug)
    setForm(f => ({ ...f, subCategoryName: sub.name || f.subCategorySlug, subCategoryId: String(sub.id || '') }))
  }, [subcatList, form.subCategorySlug, editMode])

  const handleCatScroll = useCallback(() => {
    const el = catListRef.current
    if (!el || !catHasMore || catsLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadCategories(catPage + 1, true)
    }
  }, [catHasMore, catsLoading, catPage, loadCategories])

  useEffect(() => {
    if (!catOpen) return
    const handler = (e) => { if (catDropRef.current && !catDropRef.current.contains(e.target)) setCatOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [catOpen])

  /* ── coordinator auto-fetch ── */
  const [coordinatorLoading, setCoordinatorLoading] = useState(false)
  useEffect(() => {
    if (!fetchJson || !form.categorySlug || !form.subCategorySlug || !form.customerType) return
    setCoordinatorLoading(true)
    const params = new URLSearchParams({
      category: form.categorySlug,
      sub_category: form.subCategorySlug,
      customer_type: form.customerType,
      ...(form.regionId ? { region: form.regionId } : {}),
    })
    fetchJson(`/api/categories/get-coordinator?${params}`)
      .then(res => {
        const coord = res?.coordinator || res
        const name = coord?.name || coord?.coordinator_name || ''
        const id = coord?.id || coord?.coordinator_id || ''
        setForm(f => ({ ...f, coordinatorId: id, coordinatorName: name }))
      })
      .catch((err) => {
        if (err?.status === 400 || err?.status === 404) {
          setForm(f => ({ ...f, coordinatorId: '', coordinatorName: '' }))
        }
      })
      .finally(() => setCoordinatorLoading(false))
  }, [form.categorySlug, form.subCategorySlug, form.customerType, form.regionId, fetchJson])

  /* ── fetch previous tickets when follow_up is selected and phone is available ── */
  useEffect(() => {
    if (form.ticketType !== 'follow_up' || !form.phone || !fetchJson) {
      setPrevTicketsList([])
      set('previousTicketId', '')
      return
    }
    setPrevTicketsLoading(true)
    fetchJson(`/api/tickets/ticket/customer-tickets?phone=${encodeURIComponent(form.phone)}&count=2`)
      .then(res => setPrevTicketsList(res?.tickets || []))
      .catch(() => setPrevTicketsList([]))
      .finally(() => setPrevTicketsLoading(false))
  }, [form.ticketType, form.phone, fetchJson])

  /* ── fetch disposition list once on mount ── */
  useEffect(() => {
    if (!fetchJson) return
    setDispositionLoading(true)
    fetchJson('/api/categories/disposition').then(data => setDispositionList(Array.isArray(data) ? data : (data?.results || []))).catch(() => { }).finally(() => setDispositionLoading(false))
  }, [fetchJson])

  /* ── previous tickets (follow-up type) ── */
  const [prevTicketsList, setPrevTicketsList] = useState([])
  const [prevTicketsLoading, setPrevTicketsLoading] = useState(false)

  /* ── disposition — fetched from API ── */
  const [dispositionList, setDispositionList] = useState([])
  const [dispositionLoading, setDispositionLoading] = useState(false)

  /* ── station searchable dropdown ── */
  const [stationOpen, setStationOpen] = useState(false)
  const [stationSearch, setStationSearch] = useState('')
  const [stationSelectedName, setStationSelectedName] = useState('')
  const stationDropRef = useRef(null)
  const stationListRef = useRef(null)
  const stationsLoadingRef = useRef(false)
  const stationSearchDebounce = useRef(null)

  /* ── attachments ── */
  const [attachments, setAttachments] = useState([])
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length) setAttachments(prev => [...prev, ...files])
    e.target.value = ''
  }

  const removeAttachment = (index) => setAttachments(prev => prev.filter((_, i) => i !== index))

  /* ── regions list — fetched on mount ── */
  const [regionsList, setRegionsList] = useState([])
  const [regionsLoading, setRegionsLoading] = useState(true)
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/geographies/geography/regions')
      .then(data => setRegionsList(Array.isArray(data) ? data : (data?.data || data?.results || [])))
      .catch(() => { })
      .finally(() => setRegionsLoading(false))
  }, [fetchJson])

  /* ── cities list — dependent on selected region ── */
  const [citiesList, setCitiesList] = useState([])
  const [citiesLoading, setCitiesLoading] = useState(false)
  useEffect(() => {
    if (!fetchJson) return
    setCitiesList([])
    if (!form.regionSlug) return
    setCitiesLoading(true)
    const params = new URLSearchParams({ region: form.regionSlug })
    fetchJson(`/api/geographies/geography/cities?${params}`)
      .then(data => {
        setCitiesList(Array.isArray(data) ? data : (data?.data || data?.results || []))
      })
      .catch(() => { })
      .finally(() => setCitiesLoading(false))
  }, [fetchJson, form.regionSlug])

  /* ── states list — fetched on mount ── */
  const [statesList, setStatesList] = useState([])
  const [statesLoading, setStatesLoading] = useState(true)
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/geographies/geography/states')
      .then(data => setStatesList(Array.isArray(data) ? data : (data?.data || data?.results || [])))
      .catch(() => { })
      .finally(() => setStatesLoading(false))
  }, [fetchJson])

  /* ── stations list — paginated with infinite scroll ── */
  const [stationsList, setStationsList] = useState([])
  const [stationsLoading, setStationsLoading] = useState(false)
  const [stationsPage, setStationsPage] = useState(1)
  const [stationsHasMore, setStationsHasMore] = useState(false)

  useEffect(() => {
    if (!editMode || !form.stationId || stationsList.length === 0) return
    const s = stationsList.find(x => x.id === form.stationId)
    if (!s) return
    const name = s.name || s.slug || s.id
    setStationSelectedName(name)
    setStationSearch(name)
  }, [stationsList, form.stationId, editMode])

  const loadStations = useCallback(async (citySlug, page = 1, search = '', append = false) => {
    if (!fetchJson || !citySlug || stationsLoadingRef.current) return
    stationsLoadingRef.current = true
    setStationsLoading(true)
    try {
      const params = new URLSearchParams({ city: citySlug, page: String(page) })
      if (search) params.set('q', search)
      const data = await fetchJson(`/api/geographies/station?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setStationsList(prev => append ? [...prev, ...results] : results)
      setStationsHasMore(!!data?.next)
      setStationsPage(page)
    } catch {
      if (!append) setStationsList([])
    } finally {
      stationsLoadingRef.current = false
      setStationsLoading(false)
    }
  }, [fetchJson])

  useEffect(() => {
    if (!form.citySlug) { setStationsList([]); setStationsHasMore(false); setStationsPage(1); return }
    loadStations(form.citySlug, 1, '', false)
  }, [form.citySlug, loadStations])

  useEffect(() => {
    if (!stationOpen || !form.citySlug) return
    clearTimeout(stationSearchDebounce.current)
    if (stationSearch.length === 1) return
    stationSearchDebounce.current = setTimeout(() => loadStations(form.citySlug, 1, stationSearch, false), stationSearch.length >= 2 ? 300 : 0)
    return () => clearTimeout(stationSearchDebounce.current)
  }, [stationSearch, stationOpen, form.citySlug, loadStations])

  const handleStationScroll = useCallback(() => {
    const el = stationListRef.current
    if (!el || !stationsHasMore || stationsLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadStations(form.citySlug, stationsPage + 1, stationSearch, true)
    }
  }, [stationsHasMore, stationsLoading, stationsPage, stationSearch, form.citySlug, loadStations])

  /* close v/c dropdowns on outside click */
  useEffect(() => {
    function handler(e) {
      if (vDropRef.current && !vDropRef.current.contains(e.target)) { setVResults([]); setVLastSearch('') }
      if (cDropRef.current && !cDropRef.current.contains(e.target)) { setCResults([]); setCLastSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  /* close station dropdown on outside click — same pattern as coordinator */
  useEffect(() => {
    if (!stationOpen) return
    const handler = (e) => {
      if (stationDropRef.current && !stationDropRef.current.contains(e.target)) setStationOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [stationOpen])

  /* reset station selection when city changes */
  useEffect(() => {
    setStationSelectedName('')
    setStationSearch('')
    setStationOpen(false)
  }, [form.city])

  /* ── helpers ── */
  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const loadSubCategories = useCallback(async (catSlug, page = 1, append = false) => {
    if (!fetchJson || !catSlug || subcatLoadingRef.current) return
    subcatLoadingRef.current = true
    setSubcatsLoading(true)
    try {
      const params = new URLSearchParams({ category: catSlug, page: String(page) })
      if (queryMode) params.set('category_type', 'query')
      const data = await fetchJson(`/api/categories/category?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setSubcatList(prev => append ? [...prev, ...results] : results)
      setSubcatHasMore(!!data?.next)
      setSubcatPage(page)
    } catch {
      if (!append) setSubcatList([])
    } finally {
      subcatLoadingRef.current = false
      setSubcatsLoading(false)
    }
  }, [fetchJson, queryMode])

  const handleCategoryChange = (catSlug) => {
    const cat = catList.find(c => (c.slug || c.name) === catSlug)
    setForm(f => ({
      ...f,
      categorySlug: catSlug,
      categoryName: cat?.name || catSlug,
      categoryId: cat?.id || '',
      subCategorySlug: '', subCategoryName: '', subCategoryId: '',
    }))
    setSubcatList([])
    setSubcatHasMore(false)
    setSubcatPage(1)
    setSubcatSelectedName('')
    setSubcatOpen(false)
    if (catSlug) loadSubCategories(catSlug, 1, false)
  }

  const handleSubcatScroll = useCallback(() => {
    const el = subcatListRef.current
    if (!el || !subcatHasMore || subcatsLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadSubCategories(form.categorySlug, subcatPage + 1, true)
    }
  }, [subcatHasMore, subcatsLoading, subcatPage, form.categorySlug, loadSubCategories])

  useEffect(() => {
    if (!subcatOpen) return
    const handler = (e) => { if (subcatDropRef.current && !subcatDropRef.current.contains(e.target)) setSubcatOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [subcatOpen])

  const handleSubCategoryChange = (subSlug, subName, subId) => {
    setForm(f => ({
      ...f,
      subCategorySlug: subSlug,
      subCategoryName: subName || subSlug,
      subCategoryId: subId || '',
    }))
  }

  /* ── pre-fill customer details from call data or restored previous ticket ── */
  useEffect(() => {
    const restorePrevious = localStorage.getItem('crm_restore_previous') === '1'
    if (!fromCall && !restorePrevious) return
    if (autoSearchedRef.current) return
    autoSearchedRef.current = true
    try {
      if (restorePrevious) localStorage.removeItem('crm_restore_previous')
      const callData = JSON.parse(localStorage.getItem('ozonetel_call_data') || '{}')
      const cd = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')?.customer_details || {}
      setForm(f => ({
        ...f,
        phone: cd.phone || callData.customerPhone || f.phone,
        riderName: cd.name || f.riderName,
        email: cd.email || f.email,
        customerType: cd.customer_type || f.customerType,
        experience: cd.experience || f.experience,
        language: cd.language || f.language,
        preferredChannel: cd.preferred_channel || f.preferredChannel,
        channel: cd.source_channel || (fromCall ? 'call' : f.channel),
      }))
    } catch { }
  }, [fromCall])

  /* ── vehicle search ── */
  const searchVehicles = useCallback((q) => {
    clearTimeout(vDebounce.current)
    if (!q.trim() || !fetchJson) { setVResults([]); setVLastSearch(''); return }
    vDebounce.current = setTimeout(() => {
      setVSearching(true)
      fetchJson(`/api/vehicles/vehicle/search?q=${encodeURIComponent(q)}`)
        .then(data => { setVLastSearch(q); setVResults(normalizeVehicleResults(data)) })
        .catch(() => { setVLastSearch(q); setVResults([]) })
        .finally(() => setVSearching(false))
    }, 300)
  }, [fetchJson])

  const selectVehicle = (v) => {
    setVehicleFound(v)
    setVResults([])
    setVLastSearch('')
    setVSearch('')
    setSelectedVinSuggestionId(null)
    const vd = v.vehicle_details || {}
    setForm(f => ({
      ...f,
      // clear all vehicle fields first, then fill from new selection
      vin: vd.vin_number || vd.vin || '',
      registrationNumber: vd.registration_number || '',
      vehicleId: vd.id ? String(vd.id) : '',
      batteryId: vd.battery_id || '',
      vehicleType: normalizeVehicleType(vd.vehicle_type || vd.type_of_vehicle),
      oemBrandName: vd.oem_brand_name || vd.oem || '',
      serviceProvider: vd.service_provider || '',
      cncTransactionId: vd.cnc_transaction_id || '',
      cncErrorCode: vd.cnc_error_code || '',
      experience: vd.vehicle_age || f.experience,
      customerType: normalizeCustomerType(vd.account) || f.customerType,
    }))
  }

  // Fallback for when the VIN search comes back empty — try the swap-details
  // API directly with the same search text. If it has anything, prefill the
  // whole form (nothing else has touched these fields yet, so this overwrites
  // freely). If it also has nothing, tell the agent to fill in manually.
  const handleFetchFromSwapDetails = async (rawVin) => {
    const vin = (rawVin || '').trim()
    if (!vin || !fetchJson) return
    setNoResultSwapLoading(true)
    try {
      const data = await fetchJson(`/api/vehicles/vehicle/swap-details/?vin=${encodeURIComponent(vin)}`)
      // Mark this VIN as already fetched so the auto VIN-watch effect below
      // doesn't immediately re-fetch the same thing once form.vin updates.
      swapDetailsVinRef.current = data?.vehicle_details?.vin_number || data?.vehicle_details?.vin || vin
      const { hasVehicleData, hasSwapData } = applySwapDetails(data, { fillEmptyOnly: false })
      if (!hasVehicleData && !hasSwapData) {
        set('vin', vin)
        showToast(false, "We don't have data for this VIN. Please fill in the details manually.", false, 'No Swap Details Found')
      } else {
        showToast(true, 'Prefilled ticket details from swap records.', false, 'Details Fetched')
      }
    } catch {
      showToast(false, 'Failed to fetch swap details. Please fill in the details manually.', false, 'Fetch Failed')
    } finally {
      setNoResultSwapLoading(false)
      setVResults([])
      setVLastSearch('')
      setVSearch('')
    }
  }

  // Fetches the rider's known vehicles as soon as their phone is known (an
  // inbound call auto-fills form.phone) so the agent can pick one instead of
  // typing a VIN/reg number into the search box.
  useEffect(() => {
    if (!fetchJson || !fromCall || !form.phone || vinSuggestionsFetchedRef.current) return
    vinSuggestionsFetchedRef.current = true
    setVinSuggestionsLoading(true)
    // URLSearchParams/encodeURIComponent both percent-encode '+' to %2B — keep
    // it literal since the backend expects "+91..." in the query string.
    const phoneParam = encodeURIComponent(form.phone).replace(/%2B/g, '+')
    fetchJson(`/api/tickets/ticket/vehicle-suggestion/?phone=${phoneParam}`)
      .then(data => setVinSuggestions(Array.isArray(data) ? data : (data?.vehicles || data?.results || data?.data || [])))
      .catch(() => setVinSuggestions([]))
      .finally(() => setVinSuggestionsLoading(false))
  }, [fetchJson, fromCall, form.phone])

  // A suggestion only carries summary fields — re-run it through the same
  // search endpoint the manual search box uses so the full vehicle_details/
  // customer_details payload comes back, then fill the form exactly as a
  // manual search result would.
  const selectVinSuggestion = async (suggestion) => {
    const q = suggestion.vin || suggestion.registration_number
    if (!q || !fetchJson) return
    setSelectingSuggestionId(suggestion.id)
    try {
      const data = await fetchJson(`/api/vehicles/vehicle/search?q=${encodeURIComponent(q)}`)
      const results = normalizeVehicleResults(data)
      if (results[0]) {
        selectVehicle(results[0])
        setSelectedVinSuggestionId(suggestion.id)
      } else {
        showToast(false, 'No matching vehicle found for this suggestion.')
      }
    } catch {
      showToast(false, 'Failed to load vehicle details for this suggestion.')
    } finally {
      setSelectingSuggestionId(null)
    }
  }

  // Fetch swap-details whenever form.vin reaches a full 17-char VIN, regardless
  // of how it got there (typed manually, picked from search, or a suggestion
  // click). Only fills fields that are still empty (fillEmptyOnly: true) — if
  // the search API or agent already filled a field, this never overwrites it.
  // The API returns every swap attempt for that VIN — the most recent (first)
  // one prefills CNC Error Code / CNC Transaction ID, the rest become pickable
  // alternatives in a dropdown on those two fields. The Station ID display-text
  // match is deferred to the effect below since it depends on stationsList,
  // which usually isn't loaded yet at this point (city/region come later in
  // the form).
  useEffect(() => {
    const vin = form.vin.trim()
    clearTimeout(swapDetailsDebounce.current)
    if (!fetchJson || vin.length !== 17 || vin === swapDetailsVinRef.current) return
    swapDetailsDebounce.current = setTimeout(() => {
      swapDetailsVinRef.current = vin
      setSwapDetailsLoading(true)
      fetchJson(`/api/vehicles/vehicle/swap-details/?vin=${encodeURIComponent(vin)}`)
        .then(data => { applySwapDetails(data, { fillEmptyOnly: true }) })
        .catch(() => { })
        .finally(() => setSwapDetailsLoading(false))
    }, 300)
    return () => clearTimeout(swapDetailsDebounce.current)
  }, [form.vin, fetchJson, applySwapDetails])

  // Applies an alternate swap attempt picked from either the CNC Error Code or
  // CNC Transaction ID dropdown — the two fields always come from the same
  // record, so selecting from either one updates both.
  const applySwapEntry = (entry) => {
    setForm(f => ({
      ...f,
      cncErrorCode: entry.error_code != null ? String(entry.error_code) : f.cncErrorCode,
      cncTransactionId: entry.transaction_id || f.cncTransactionId,
    }))
    setCncErrorDropdownOpen(false)
    setCncTxnDropdownOpen(false)
  }

  useEffect(() => {
    if (!cncErrorDropdownOpen) return
    const handler = (e) => { if (cncErrorDropRef.current && !cncErrorDropRef.current.contains(e.target)) setCncErrorDropdownOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [cncErrorDropdownOpen])

  useEffect(() => {
    if (!cncTxnDropdownOpen) return
    const handler = (e) => { if (cncTxnDropRef.current && !cncTxnDropRef.current.contains(e.target)) setCncTxnDropdownOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [cncTxnDropdownOpen])

  // Sets the Station ID field's *display text* once stationsList has (re)loaded
  // for the swap's city. Two cases:
  // - Full station object (has a known name already): the form fields
  //   (stationId/stationSlug/nearbyStationId) were already set directly above;
  //   this just waits for stationsList to load — which also means the
  //   `[form.city]` reset effect (that clears the display text on every city
  //   change) has already fired and settled — before showing the name, so it
  //   doesn't get wiped out by that reset.
  // - Legacy code/name-only station (no id): search stationsList for a match
  //   and apply the field updates here instead, same as clicking it manually.
  useEffect(() => {
    if (!pendingSwapStation || stationsList.length === 0) return
    let stationCode = pendingSwapStation.code
    let stationName = pendingSwapStation.name
    if (!stationCode) {
      const match = stationsList.find(s =>
        (pendingSwapStation.code && s.station_code === pendingSwapStation.code) ||
        (pendingSwapStation.name && (s.name || '').toLowerCase() === pendingSwapStation.name.toLowerCase())
      )
      if (match) {
        stationCode = match.station_code || match.slug || match.id
        setForm(f => ({
          ...f,
          stationId: match.id || match.slug,
          stationSlug: match.slug || '',
          area: f.area || match.name || f.area,
          ...(f.subCategorySlug !== 'battery-stuck-at-vehicle' ? { nearbyStationId: match.station_code || '' } : {}),
        }))
      }
    }
    // Station ID field shows the code (matches manual selection); name went to Incident Area already.
    const displayText = stationCode || stationName
    if (displayText) {
      setStationSelectedName(displayText)
      setStationSearch(displayText)
    }
    setPendingSwapStation(null)
  }, [stationsList, pendingSwapStation])

  /* ── customer search ── */
  const searchCustomers = useCallback((q) => {
    clearTimeout(cDebounce.current)
    if (!q.trim() || !fetchJson) { setCResults([]); setCLastSearch(''); return }
    cDebounce.current = setTimeout(() => {
      setCSearching(true)
      fetchJson(`/api/customers/customer/search/?q=${encodeURIComponent(q)}`)
        .then(data => {
          setCLastSearch(q)
          const arr = Array.isArray(data) ? data : (data?.results ? data.results : (data ? [data] : []))
          setCResults(arr.filter(r => r?.customer_details?.name || r?.customer_details?.phone || r?.name || r?.phone))
        })
        .catch(() => { setCLastSearch(q); setCResults([]) })
        .finally(() => setCSearching(false))
    }, 300)
  }, [fetchJson])

  const selectCustomerResult = (r) => {
    setCResults([])
    setCSearch('')
    setCLastSearch('')
    const cd = r.customer_details || r
    const vd = r.vehicle_details || {}
    setForm(f => ({
      ...f,
      // clear all customer fields first, then fill from new selection
      phone: cd.phone || '',
      email: cd.email || '',
      preferredChannel: cd.preferred_channel || '',
      channel: cd.source_channel || '',
      customerType: cd.customer_type || f.customerType,
      experience: cd.experience || f.experience,
      language: cd.language || '',
    }))
  }

  // Incident Area is only mandatory for Battery stuck → Battery stuck in vehicle;
  // every other category/sub-category combination leaves it optional.
  const incidentAreaRequired =
    ((form.categorySlug || '').toLowerCase() === 'battery-stuck' || (form.categoryName || '').trim().toLowerCase() === 'battery stuck') &&
    ((form.subCategorySlug || '').toLowerCase() === 'battery-stuck-in-vehicle' || (form.subCategoryName || '').trim().toLowerCase() === 'battery stuck in vehicle')

  /* ── submit ── */
  const handleSubmit = async (action = '') => {
    if (!form.vin.trim()) {
      showToast(false, 'Please enter Vehicle VIN No.')
      return
    }
    if (!form.categoryName || !form.subCategoryName) {
      showToast(false, 'Please select Issue Category and Sub-Category.')
      return
    }
    if (!queryMode) {
      if (!form.vehicleType) {
        showToast(false, 'Please select Type of Vehicle.')
        return
      }
      if (!form.oemBrandName) {
        showToast(false, 'Please select OEM Brand Name.')
        return
      }
      if (!form.serviceProvider) {
        showToast(false, 'Please select Service Provider.')
        return
      }
      if (!form.cncErrorCode) {
        showToast(false, 'Please select CNC Error Code.')
        return
      }
      if (!form.registrationNumber.trim()) {
        showToast(false, 'Please enter Registration No.')
        return
      }
      if (!form.cncTransactionId.trim()) {
        showToast(false, 'Please enter CNC Transaction ID or mark it as N/A.')
        return
      }
      if (!form.channel) {
        showToast(false, 'Please select Source Channel.')
        return
      }
      if (!form.customerType) {
        showToast(false, 'Please select Customer Type.')
        return
      }
      if (!form.experience) {
        showToast(false, 'Please select Vehicle Age.')
        return
      }
      if (!form.locationType) {
        showToast(false, 'Please select a Location Type.')
        return
      }
      if (!form.regionId) {
        showToast(false, 'Please select a Region.')
        return
      }
      if (!form.ticketType) {
        showToast(false, 'Please select Type of Ticket.')
        return
      }
      if (!form.description.trim()) {
        showToast(false, 'Please enter a description.')
        return
      }
      if (incidentAreaRequired && !form.area.trim()) {
        showToast(false, 'Please enter Incident Area.')
        return
      }
      if (!form.city) {
        showToast(false, 'Please select an Incident City.')
        return
      }
      if (!form.stationId) {
        showToast(false, 'Please select a Station ID.')
        return
      }
    }
    if (!fetchJson) { showToast(false, 'Not connected.'); return }

    if (action === 'resolve_now') setResolvingNow(true)
    else setSubmitting(true)

    let autoTicketId = null
    if (fromCall) {
      try {
        const autoTicket = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')
        autoTicketId = autoTicket?.id || autoTicket?.ticket_id || null
      } catch { }
    }

    try {
      const payload = {
        ticket_id: editMode ? editTicketId : autoTicketId,
        vehicle_details: {
          vehicle_id: form.vehicleId || null,
          vin_number: form.vin || null,
          registration_number: form.registrationNumber || null,
          battery_id: form.batteryId || null,
          qis_id: form.qisId || null,
          type_of_vehicle: form.vehicleType || null,
          oem: form.oemBrandName || null,
          service_provider: form.serviceProvider || null,
          cnc_error_code: form.cncErrorCode || null,
          cnc_transaction_id: form.cncTransactionId || null,
          vehicle_latest_swap: swapDetailsList?.[0] || null,
        },
        swap_details: swapDetailsRaw,
        customer_details: {
          name: form.riderName || null,
          email: form.email || null,
          phone: form.phone || null,
          preferred_channel: form.preferredChannel || null,
          customer_type: form.customerType || null,
          source_channel: form.channel || null,
          language: form.language || null,
          vehicle_age: form.experience || null,
        },
        issue_details: {
          category: form.categorySlug || null,
          sub_category: form.subCategorySlug || null,
          category_type: queryMode ? 'query' : 'normal',
          changed_assigned_coordinator: null,
          ticket_type: form.ticketType || null,
          sub_ticket_type: queryMode ? 'query_ticket' : 'new_ticket',
          disposition: form.disposition || null,
          previous_ticket: form.previousTicketId || null,
          priority: form.priority || null,
          location_type: form.locationType || null,
          region: form.regionId || null,
          station: form.stationId || null,
          incident_state: form.stateId || null,
          incident_city: form.city || null,
          incident_area: form.area || null,
          dock_id: form.dockId || null,
          nearby_station: form.nearbyStationId || null,
          descripition: form.description || null,
        },
      }
      let res
      const endpoint = action
        ? `/api/tickets/ticket/create-ticket/?action=${action}`
        : '/api/tickets/ticket/create-ticket/'
      res = await fetchJson(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (editMode) {
        showToast(true, 'Ticket updated successfully!')
        setTimeout(() => { if (onEditSuccess) onEditSuccess() }, 800)
      } else {
        const newTicketId = res?.ticket_id || res?.id || null
        

        // Upload attachments if any — transparent to the user
        let attachmentWarning = ''
        if (attachments.length > 0 && newTicketId) {
          try {
            const formData = new FormData()
            attachments.forEach(file => formData.append('files', file))
            await fetchJson(
              `/api/tickets/ticket/add-attachments/${newTicketId}/?attachment_type=create_ticket`,
              { method: 'POST', body: formData },
            )
          } catch (attachErr) {
            attachmentWarning = ` (Attachments failed: ${attachErr?.message || 'upload error'})`
          }
        }

        const ticketForDetail = { ticket_id: newTicketId, ticket_number: res?.ticket_number || '' }
        setCreatedTicket(ticketForDetail)
        const ticketLabel = res?.ticket_number ? `#${res.ticket_number}` : ''
        showToast(!attachmentWarning, `Ticket ${ticketLabel} created successfully!${attachmentWarning}`, true)
        if (setSelectedTicket && setCurrentPage) {
          setTimeout(() => {
            setSelectedTicket(ticketForDetail)
            setCurrentPage('ticket-detail')
          }, 1000)
        }
        localStorage.removeItem(DRAFT_KEY)
        if (fromCall) {
          ticketCreatedRef.current = true
          localStorage.removeItem('ozonetel_auto_ticket')
          localStorage.removeItem('ozonetel_call_log_id')
          localStorage.removeItem('ozonetel_call_data')
        }
        setForm(emptyForm())
        setAttachments([])
        setVehicleFound(null); setShowAllTickets(false)
        setVSearch('')
      }
    } catch (err) {
      const detail = err?.detail || err?.message || ''
      showToast(false, detail ? `Failed to ${editMode ? 'update' : 'create'} ticket: ${detail}` : `Failed to ${editMode ? 'update' : 'create'} ticket. Please try again.`)
    } finally {
      if (action === 'resolve_now') setResolvingNow(false)
      else setSubmitting(false)
    }
  }

  /* returns { phone, channel } pre-filled from call localStorage — used when resetting while fromCall */
  const getCallPreFill = () => {
    if (!fromCall) return {}
    try {
      const callData = JSON.parse(localStorage.getItem('ozonetel_call_data') || '{}')
      const ticketData = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')
      const phone = ticketData?.customer_details?.phone || callData.customerPhone || ''
      return { phone, channel: 'call' }
    } catch { return {} }
  }

  const handleReset = () => {
    setForm({ ...emptyForm(), ...getCallPreFill() })
    setVehicleFound(null); setShowAllTickets(false)
    setVSearch(''); setVResults([])
    setCSearch(''); setCResults([]); setCLastSearch('')
    setCatSelectedName(''); setCatOpen(false)
    setSubcatSelectedName(''); setSubcatOpen(false)
    setStationSelectedName(''); setStationSearch(''); setStationOpen(false)
    setAttachments([])
  }

  /* ── issue + submit block (shared between auto and manual modes) ── */
  const issueBlock = (
    <div className="form-section">
      <div className="fshdr">Issue Details</div>

      {whatsappHint && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px', marginBottom: 12,
          background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', marginBottom: 2 }}>WhatsApp Category</div>
            <div style={{ fontSize: 12, color: '#166534' }}>{whatsappHint}</div>
          </div>
        </div>
      )}

      <div className="fg">

        {/* Category */}
        <div className="fgr">
          <label>Issue Category <span className="req">*</span></label>
          {catsError ? (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: '#DC2626' }}>Failed to load — </span>
              <button onClick={() => loadCategories(1, false)} style={{ fontSize: '11px', color: '#1B4FD8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>retry</button>
            </div>
          ) : (
            <div ref={catDropRef} style={{ position: 'relative' }}>
              <div
                onClick={() => setCatOpen(o => !o)}
                style={{
                  border: `1px solid ${catOpen ? '#6366F1' : '#E0E7EF'}`, borderRadius: 8, padding: '9px 12px',
                  cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: '#fff', fontSize: 13, color: form.categorySlug ? '#111827' : '#9CA3AF', minHeight: 38,
                }}
              >
                <span>{catsLoading && catList.length === 0 ? 'Loading…' : (form.categorySlug ? catSelectedName : 'Select category…')}</span>
                <span style={{ color: '#9CA3AF', fontSize: 10, marginLeft: 8 }}>▼</span>
              </div>
              {catOpen && (
                <div
                  ref={catListRef}
                  onScroll={handleCatScroll}
                  style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                    boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                  }}
                >
                  <div
                    onClick={() => { handleCategoryChange(''); setCatSelectedName(''); setCatOpen(false) }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    Select category…
                  </div>
                  {catList.map(c => (
                    <div
                      key={c.id || c.slug}
                      onClick={() => { handleCategoryChange(c.slug || c.name); setCatSelectedName(c.name); setCatOpen(false) }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                        background: form.categorySlug === (c.slug || c.name) ? '#EFF6FF' : undefined,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = form.categorySlug === (c.slug || c.name) ? '#DBEAFE' : '#F8FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = form.categorySlug === (c.slug || c.name) ? '#EFF6FF' : '#fff'}
                    >
                      <div style={{ fontSize: 13, color: '#111827' }}>{c.name}</div>
                    </div>
                  ))}
                  {catsLoading && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>
                  )}
                  {!catsLoading && catList.length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No categories found</div>
                  )}
                  {!catsLoading && catHasMore && (
                    <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sub-category */}
        <div className="fgr">
          <label>Issue Sub Category <span className="req">*</span></label>
          <div ref={subcatDropRef} style={{ position: 'relative' }}>
            <div
              onClick={() => { if (form.categorySlug) setSubcatOpen(o => !o) }}
              style={{
                border: `1px solid ${subcatOpen ? '#6366F1' : '#E0E7EF'}`, borderRadius: 8, padding: '9px 12px',
                cursor: form.categorySlug ? 'pointer' : 'not-allowed', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: form.categorySlug ? '#fff' : '#F8FAFC', fontSize: 13,
                color: form.subCategorySlug ? '#111827' : '#9CA3AF', minHeight: 38,
              }}
            >
              <span>
                {!form.categorySlug ? 'Select category first' : subcatsLoading && subcatList.length === 0 ? 'Loading…' : (form.subCategorySlug ? subcatSelectedName : 'Select sub-category…')}
              </span>
              <span style={{ color: '#9CA3AF', fontSize: 10, marginLeft: 8 }}>▼</span>
            </div>
            {subcatOpen && (
              <div
                ref={subcatListRef}
                onScroll={handleSubcatScroll}
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                  boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                }}
              >
                <div
                  onClick={() => { handleSubCategoryChange('', '', ''); setSubcatSelectedName(''); setSubcatOpen(false) }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  Select sub-category…
                </div>
                {subcatList.map(s => (
                  <div
                    key={s.id || s.slug}
                    onClick={() => { handleSubCategoryChange(s.slug || s.name, s.name, s.id); setSubcatSelectedName(s.name); setSubcatOpen(false) }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                      background: form.subCategorySlug === (s.slug || s.name) ? '#EFF6FF' : undefined,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = form.subCategorySlug === (s.slug || s.name) ? '#DBEAFE' : '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = form.subCategorySlug === (s.slug || s.name) ? '#EFF6FF' : '#fff'}
                  >
                    <div style={{ fontSize: 13, color: '#111827' }}>{s.name}</div>
                  </div>
                ))}
                {subcatsLoading && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>
                )}
                {!subcatsLoading && subcatList.length === 0 && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No sub-categories found</div>
                )}
                {!subcatsLoading && subcatHasMore && (
                  <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                )}
              </div>
            )}
          </div>
        </div>

        {!queryMode && (
          <>
            {/* Region */}
            <div className="fgr">
              <label>Region <span className="req">*</span></label>
              <select className={form.regionId ? 'selected' : ''} value={form.regionId} onChange={e => {
                const r = regionsList.find(x => x.id === e.target.value)
                setForm(f => ({ ...f, regionId: e.target.value, regionSlug: r?.slug || '', city: '', citySlug: '', stationId: '', stationSlug: '' }))
                setStationSelectedName(''); setStationSearch('')
              }} disabled={regionsLoading}>
                <option value="">{regionsLoading ? 'Loading…' : 'Select region…'}</option>
                {regionsList.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>

            {/* Coordinator Assigned (read-only, from API) */}
            <div className="fgr">
              <label>Co-ordinator Assigned</label>
              <input
                value={coordinatorLoading ? 'Loading…' : (form.coordinatorName || '')}
                readOnly
                placeholder="Auto-assigned based on category"
                style={{ background: '#F8FAFC', color: '#374151', cursor: 'default' }}
              />
            </div>

            {/* Type of Ticket */}
            <div className="fgr">
              <label>Type of Ticket <span className="req">*</span></label>
              <select className={form.ticketType ? 'selected' : ''} value={form.ticketType} onChange={e => {
                const val = e.target.value
                if (val === 'self_assign') {
                  setForm(f => ({ ...f, ticketType: val, coordinatorId: '', coordinatorName: '' }))
                } else {
                  setForm(f => ({ ...f, ticketType: val, disposition: '' }))
                }
              }}>
                <option value="">Select type…</option>
                {TICKET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Previous Ticket — only for Follow Up type */}
            {form.ticketType === 'follow_up' && (
              <div className="fgr">
                <label>Previous Ticket</label>
                <select
                  className={form.previousTicketId ? 'selected' : ''}
                  value={form.previousTicketId}
                  onChange={e => set('previousTicketId', e.target.value)}
                  disabled={prevTicketsLoading || !form.phone}
                >
                  <option value="">
                    {!form.phone ? 'Enter phone number first' : prevTicketsLoading ? 'Loading…' : prevTicketsList.length === 0 ? 'No previous tickets found' : 'Select previous ticket…'}
                  </option>
                  {prevTicketsList.map(t => (
                    <option key={t.id} value={t.id}>{t.ticket_number}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Disposition — only for Self Assign */}
            {form.ticketType === 'self_assign' && (
              <div className="fgr">
                <label>Disposition</label>
                <select
                  className={form.disposition ? 'selected' : ''}
                  value={form.disposition}
                  onChange={e => set('disposition', e.target.value)}
                  disabled={dispositionLoading}
                >
                  <option value="">{dispositionLoading ? 'Loading…' : 'Select disposition…'}</option>
                  {dispositionList.map(d => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Priority */}
            <div className="fgr">
              <label>Priority <span className="req">*</span></label>
              <select className={form.priority ? 'selected' : ''} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            {/* Location Type */}
            <div className="fgr">
              <label>Location Type <span className="req">*</span></label>
              <select className={form.locationType ? 'selected' : ''} value={form.locationType} onChange={e => set('locationType', e.target.value)}>
                <option value="">Select location type…</option>
                {LOCATION_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>

            {/* Incident City — from /api/geographies/geography/cities */}
            <div className="fgr">
              <label>Incident City <span className="req">*</span></label>
              <select
                className={form.city ? 'selected' : ''}
                value={form.city}
                onChange={e => {
                  const c = citiesList.find(x => x.id === e.target.value)
                  setForm(f => ({ ...f, city: e.target.value, citySlug: c?.slug || '', stationId: '', stationSlug: '' }))
                }}
                disabled={citiesLoading || !form.regionId}
              >
                <option value="">{!form.regionId ? 'Select region first' : citiesLoading ? 'Loading…' : 'Select city…'}</option>
                {citiesList.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Station ID — always visible */}
            <div className="fgr">
              <label>
                Station ID <span className="req">*</span>
                {swapDetailsLoading && <span style={{ marginLeft: 6, fontSize: 10, color: '#0090D9', fontWeight: 500 }}>⏳ Fetching swap details…</span>}
              </label>
              <div ref={stationDropRef} style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder={!form.city ? 'Select city first' : stationsLoading ? 'Loading…' : 'Search station…'}
                    value={stationOpen ? stationSearch : (stationSelectedName || '')}
                    onFocus={() => { if (form.city) { setStationOpen(true); setStationSearch('') } }}
                    onChange={e => setStationSearch(e.target.value)}
                    disabled={!form.city}
                    autoComplete="off"
                    style={{ paddingRight: form.stationId ? '28px' : undefined }}
                  />
                  {form.stationId && !stationOpen && (
                    <button
                      type="button"
                      onClick={() => {
                        setForm(f => ({
                          ...f,
                          stationId: '',
                          stationSlug: '',
                          ...(f.subCategorySlug !== 'battery-stuck-at-vehicle' ? { nearbyStationId: '' } : {})
                        }))
                        setStationSelectedName('')
                        // setStationSearch('')
                      }}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 0 }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {stationOpen && (
                  <div
                    ref={stationListRef}
                    onScroll={handleStationScroll}
                    style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                      background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                      boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                    }}>
                    <div
                      onClick={() => {
                        setForm(f => ({ ...f, stationId: '', stationSlug: '', ...(f.subCategorySlug !== 'battery-stuck-at-vehicle' ? { nearbyStationId: '' } : {}) }))
                        setStationSelectedName('')
                        setStationOpen(false)
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      Select station…
                    </div>
                    {stationsList
                      .filter(s => {
                        if (!stationSearch) return true
                        const q = stationSearch.toLowerCase()
                        return (s.name || '').toLowerCase().includes(q) || (s.station_code || '').toLowerCase().includes(q)
                      })
                      .map((s, i) => (
                        <div
                          key={s.id || i}
                          onClick={() => {
                            // Dropdown shows area/station name on top and the
                            // station code below — display the code (the "ID")
                            // in the Station ID field, and drop the name into
                            // Incident Area.
                            const stationCode = s.station_code || s.slug || s.id
                            const areaName = s.name || s.slug || s.id

                            setForm(f => ({
                              ...f,
                              stationId: s.id || s.slug,
                              stationSlug: s.slug || '',
                              area: areaName,
                              ...(f.subCategorySlug !== 'battery-stuck-at-vehicle'
                                ? { nearbyStationId: s.station_code || '' }
                                : {}),
                            }))

                            setStationSelectedName(stationCode)
                            setStationSearch(stationCode)
                            setStationOpen(false)
                          }}
                          style={{
                            padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                            background: form.stationId === (s.id || s.slug) ? '#EFF6FF' : undefined,
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = form.stationId === (s.id || s.slug) ? '#DBEAFE' : '#F8FAFB'}
                          onMouseLeave={e => e.currentTarget.style.background = form.stationId === (s.id || s.slug) ? '#EFF6FF' : '#fff'}
                        >
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{s.name || s.slug || s.id}</div>
                          {s.station_code && <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{s.station_code}</div>}
                        </div>
                      ))}
                    {stationsList.filter(s => {
                      if (!stationSearch) return true
                      const q = stationSearch.toLowerCase()
                      return (s.name || '').toLowerCase().includes(q) || (s.station_code || '').toLowerCase().includes(q)
                    }).length === 0 && (
                        <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No stations found</div>
                      )}
                    {!stationsLoading && stationsHasMore && (
                      <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Near by Station ID — only for battery stuck sub-categories */}
            {(['battery-stuck-at-station', 'battery-stuck-in-station', 'battery-stuck-at-vehicle', 'battery-stuck-in-vehicle'].includes(form.subCategorySlug)) && (
              <div className="fgr">
                <label>Near by Station ID</label>
                <input
                  placeholder="Nearby station ID"
                  value={form.nearbyStationId}
                  onChange={e => set('nearbyStationId', e.target.value)}
                />
              </div>
            )}

            {/* Dock ID */}
            <div className="fgr">
              <label>Dock ID</label>
              <input placeholder="Dock ID" value={form.dockId} onChange={e => set('dockId', e.target.value)} />
            </div>



            {/* Incident Area */}
            <div className="fgr">
              <label>Incident Area {incidentAreaRequired && <span className="req">*</span>}</label>
              <input placeholder="Area / Locality" value={form.area} onChange={e => set('area', e.target.value)} />
            </div>
          </>
        )}
      </div>

      {!queryMode && (
        <div className="fgr" style={{ marginTop: '10px' }}>
          <label>Description <span className="req">*</span></label>
          <textarea placeholder="Describe the issue as reported by the rider…" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
      )}

      {/* Attachments */}
      {!queryMode && (
        <div style={{ marginTop: '12px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>
            Attachments <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
          </label>
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', border: '1.5px dashed #CBD5E1',
              borderRadius: 7, background: '#F8FAFC', cursor: 'pointer',
              fontSize: 12, color: '#64748B', fontWeight: 500,
            }}
          >
            📎 Attach Files
          </button>
          {attachments.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {attachments.map((file, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 10px', background: '#F1F5F9', borderRadius: 6,
                }}>
                  <span style={{ fontSize: 12, flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📄 {file.name}
                  </span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                    {file.size < 1024 * 1024
                      ? `${(file.size / 1024).toFixed(0)} KB`
                      : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, padding: 0, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="action-row" style={{ marginTop: '14px' }}>
        {(() => {
          const initial = editInitialFormRef.current
          const hasChanges = !editMode || !initial || (() => {
            const keys = Object.keys(initial)
            return keys.some(k => (form[k] ?? '') !== (initial[k] ?? ''))
          })()
          const submitDisabled = submitting || resolvingNow || (editMode && !hasChanges)
          const resolveDisabled = submitting || resolvingNow
          return (<>
            {
              !queryMode &&
              <button className="btn btn-success" style={{ flex: 1, justifyContent: 'center', fontSize: '13px', padding: '9px' }} onClick={() => handleSubmit()} disabled={submitDisabled}>
                {submitting ? (editMode ? 'Updating…' : 'Submitting…') : (editMode ? '✓ Update Ticket' : '✓ Submit Ticket')}
              </button>
            }
            <button
              className="btn"
              style={{ flex: 1, justifyContent: 'center', fontSize: '13px', padding: '9px', background: !resolveDisabled ? '#7C3AED' : '#E5E7EB', color: !resolveDisabled ? '#fff' : '#9CA3AF', border: 'none', borderRadius: 8, fontWeight: 600, cursor: resolveDisabled ? 'not-allowed' : 'pointer' }}
              onClick={() => handleSubmit('resolve_now')}
              disabled={resolveDisabled}
            >
              {resolvingNow ? 'Resolving…' : '⚡ Resolve Now'}
            </button>
          </>)
        })()}
        {editMode
          ? <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }} onClick={onEditSuccess} disabled={submitting}>Cancel</button>
          : <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleReset} disabled={submitting || resolvingNow}>Clear</button>
        }
      </div>
    </div>
  )

  return (
    <div>
      {/* ── Draft restore modal ── */}
      {draftModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 32px', width: 400, maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              Resume previous ticket?
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
              You have an unsaved ticket form from a previous session. Do you want to continue where you left off?
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleRestoreDraft}
                style={{ flex: 1, padding: '10px 0', background: '#0090D9', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Proceed with previous
              </button>
              <button
                onClick={handleDiscardDraft}
                style={{ flex: 1, padding: '10px 0', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Create new
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast notifications ── */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              pointerEvents: 'all',
              minWidth: 300, maxWidth: 400,
              background: t.ok ? '#ECFDF5' : '#FFF1F1',
              border: `1px solid ${t.ok ? '#6EE7B7' : '#FECACA'}`,
              borderLeft: `4px solid ${t.ok ? '#10B981' : '#EF4444'}`,
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,.13)',
              padding: '12px 14px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              animation: 'slideInRight .2s ease',
            }}>
              <div style={{ fontSize: 18, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>
                {t.ok ? '✅' : '❌'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.ok ? '#065F46' : '#991B1B', lineHeight: 1.4 }}>
                  {t.title || (t.ok ? 'Ticket Created' : 'Failed to Create Ticket')}
                </div>
                <div style={{ fontSize: 12, color: t.ok ? '#047857' : '#DC2626', marginTop: 3, lineHeight: 1.4 }}>
                  {t.msg}
                </div>
                {t.withViewTickets && (
                  <button
                    onClick={() => {
                      dismissToast(t.id)
                      if (createdTicket?.ticket_id && setSelectedTicket) {
                        setSelectedTicket(createdTicket)
                        setCurrentPage?.('ticket-detail')
                      } else {
                        setCurrentPage?.('tickets')
                      }
                    }}
                    style={{ marginTop: 8, background: '#0090D9', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    View Ticket →
                  </button>
                )}
              </div>
              <button
                onClick={() => dismissToast(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Call-linked banner — shown only when opened automatically from a call */}
      {fromCall && (() => {
        let ticketNumber = urlParams.get('ticket_number') || ''
        if (!ticketNumber) {
          try { ticketNumber = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')?.ticket_number || '' } catch { }
        }
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: 8, padding: '8px 14px', marginBottom: 14,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B82F6', flexShrink: 0, animation: 'pulse 1.5s infinite' }} />
            <div style={{ fontSize: 12, color: '#1E40AF' }}>
              <strong>Auto-opened from active call</strong>
              {ticketNumber && (
                <span style={{ marginLeft: 8, color: '#3B82F6', fontFamily: 'monospace' }}>Ticket # {ticketNumber}</span>
              )}
            </div>
          </div>
        )
      })()}

      {/* Query-mode banner — limited-field ticket */}
      {queryMode && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: 8, padding: '8px 14px', marginBottom: 14,
        }}>
          <span style={{ fontSize: 14 }}>💬</span>
          <div style={{ fontSize: 12, color: '#92400E' }}>
            <strong>Query Ticket</strong> — limited fields
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      {!editMode && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: '#6B7280' }}>
        <button
          type="button"
          onClick={() => setCurrentPage?.('tickets')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, color: '#6B7280', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
          onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          ← Tickets
        </button>
        <span>/</span>
        <span style={{ color: '#111827', fontWeight: 500 }}>Create Ticket</span>
      </div>}

      {/* ══════════ VEHICLE DETAILS ══════════ */}
      <div className="form-section">
        <div className="fshdr">Vehicle Details</div>

        {fromCall && (vinSuggestionsLoading || vinSuggestions.length > 0) && (
          <div style={{ marginBottom: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0090D9', letterSpacing: 0.3, marginBottom: 8 }}>
              📞 VEHICLES LINKED TO THIS RIDER — CLICK TO AUTOFILL
            </div>
            {vinSuggestionsLoading ? (
              <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {vinSuggestions.map(s => {
                  const label = s.vin || s.registration_number || '—'
                  const selecting = selectingSuggestionId === s.id
                  const active = selectedVinSuggestionId === s.id
                  const openTickets = s.ticket_stats?.tickets_opened || 0
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={selecting}
                      onClick={() => selectVinSuggestion(s)}
                      style={{
                        textAlign: 'left', padding: '6px 12px', borderRadius: 8,
                        border: `1px solid ${active ? '#0090D9' : '#BFDBFE'}`,
                        background: active ? '#EFF6FF' : '#fff', cursor: selecting ? 'default' : 'pointer',
                        opacity: selecting ? 0.6 : 1,
                      }}
                    >
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 5 }}>
                        🚗 {label}
                        {active && <span style={{ fontSize: 10, fontWeight: 700, color: '#0090D9' }}>✓ Selected</span>}
                      </div>
                      {selecting && <div style={{ fontSize: 10, color: '#0090D9', marginTop: 4 }}>Loading details…</div>}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <div ref={vDropRef} style={{ position: 'relative', marginBottom: 16 }}>
          <div className="search-row">
            <input
              type="text"
              placeholder="Search by VIN, Reg No."
              value={vSearch}
              onChange={e => { setVSearch(e.target.value); searchVehicles(e.target.value) }}
              onKeyDown={e => e.key === 'Enter' && searchVehicles(vSearch)}
              autoComplete="off"
            />
            <button className="btn btn-primary" onClick={() => searchVehicles(vSearch)} disabled={vSearching}>
              {vSearching ? '…' : 'Search'}
            </button>

          </div>

          {/* Always available below the search bar — lets the agent pull vehicle
              data straight from swap records for whatever VIN/reg is currently
              typed, without needing a failed search first. */}
          {/* <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
            
          </div> */}

          {!vSearching && vResults.length === 0 && vLastSearch && !vehicleFound && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: '4px', padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: '#9CA3AF' }}>No results found for &ldquo;{vLastSearch}&rdquo;</div>
              <button
                type="button"
                onClick={() => handleFetchFromSwapDetails(vSearch || vLastSearch)}
                disabled={noResultSwapLoading || !(vSearch || vLastSearch).trim()}
                style={{
                  background: '#0090D9', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  cursor: (noResultSwapLoading || !(vSearch || vLastSearch).trim()) ? 'not-allowed' : 'pointer',
                  opacity: (noResultSwapLoading || !(vSearch || vLastSearch).trim()) ? 0.5 : 1,
                }}
              >
                {noResultSwapLoading ? 'Fetching swap details…' : '⚡ Fetch from Swap Details'}
              </button>
            </div>
          )}

          {vResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: '280px', overflowY: 'auto', marginTop: '4px' }}>
              {vResults.map((v, idx) => {
                const vd = v.vehicle_details || {}
                const cd = v.customer_details || {}
                const primaryLabel = vd.vin_number || vd.vin || vd.registration_number || cd.name || cd.phone || '—'
                return (
                  <div key={idx} onClick={() => selectVehicle(v)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }} onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A1F37', fontFamily: 'monospace' }}>🚗 {primaryLabel}</span>
                      {vd.model && <span style={{ fontSize: '11px', color: '#5A6672' }}>{vd.model}</span>}
                      {vd.state && <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: vd.state === 'active' ? '#ECFDF5' : '#FEF2F2', color: vd.state === 'active' ? '#047857' : '#DC2626' }}>{vd.state === 'active' ? '● Active' : vd.state}</span>}
                    </div>
                    {(vd.vin_number || vd.vin || vd.registration_number) && <div style={{ fontSize: '11px', color: '#9AA5B1', fontFamily: 'monospace', marginTop: '2px' }}>{[vd.vin_number || vd.vin ? `VIN: ${vd.vin_number || vd.vin}` : null, vd.registration_number && `Reg: ${vd.registration_number}`].filter(Boolean).join('  ·  ')}</div>}
                    {(vd.oem_brand_name || vd.oem || vd.account) && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{[vd.oem_brand_name || vd.oem ? `OEM: ${vd.oem_brand_name || vd.oem}` : null, vd.account ? `Account: ${vd.account}` : null].filter(Boolean).join('  ·  ')}</div>}
                    {(cd.name || cd.phone) && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>👤 {[cd.name, cd.phone].filter(Boolean).join(' · ')}</div>}
                    {vd.number_of_open_tickets > 0 && <div style={{ fontSize: '10px', color: '#F59E0B', marginTop: '2px', fontWeight: 600 }}>{vd.number_of_open_tickets} open ticket(s)</div>}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {vehicleFound && (() => {
          const vd = vehicleFound.vehicle_details || {}
          const stateVal = vd.vehicle_state || vd.state || ''
          const isActive = stateVal === 'active'
          const tickets = vd.previous_ticket_numbers || []
          const visibleTickets = showAllTickets ? tickets : tickets.slice(0, 2)
          return (
            <div style={{ marginBottom: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: tickets.length ? 5 : 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#065F46', fontFamily: 'monospace' }}>
                    {vd.vin_number || vd.vin || vd.registration_number || '—'}
                  </span>
                  <span style={{ background: isActive ? '#DCFCE7' : '#FEE2E2', color: isActive ? '#15803D' : '#DC2626', fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                    ● {isActive ? 'Active' : (stateVal || 'Unknown')}
                  </span>
                  {vd.number_of_open_tickets > 0 && (
                    <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                      {vd.number_of_open_tickets} open
                    </span>
                  )}
                </div>
                {(vd.oem_brand_name || vd.oem || vd.account) && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: tickets.length ? 4 : 0 }}>
                    {[vd.oem_brand_name || vd.oem ? `OEM: ${vd.oem_brand_name || vd.oem}` : null, vd.account ? `Account: ${vd.account}` : null].filter(Boolean).join('  ·  ')}
                  </div>
                )}
                {tickets.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, color: '#6B7280', flexShrink: 0 }}>Prev tickets:</span>
                    {visibleTickets.map((t, i) => (
                      <span key={i} style={{ fontSize: 10, fontFamily: 'monospace', color: '#374151', background: '#fff', border: '1px solid #D1FAE5', borderRadius: 4, padding: '1px 6px' }}>{t}</span>
                    ))}
                    {tickets.length > 2 && (
                      <button onClick={() => setShowAllTickets(v => !v)} style={{ fontSize: 10, color: '#0090D9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                        {showAllTickets ? 'see less ↑' : `+${tickets.length - 2} more ↓`}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => { const id = vehicleFound?.vehicle_details?.id; localStorage.setItem('crm_vehicle_360_data', JSON.stringify({ ...vehicleFound, vehicle_id: id, id })); const url = new URL(window.location.href); url.search = ''; url.searchParams.set('supportPage', 'vehicle-360'); url.searchParams.set('nodialer', '1'); window.open(url.toString(), '_blank', 'noopener') }}
                style={{ background: '#fff', border: '1px solid #BBF7D0', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#065F46', flexShrink: 0 }}
              >
                Vehicle 360 ↗
              </button>
            </div>
          )
        })()}

        <div className="fg">
          <div className="fgr">
            <label>Vehicle VIN No <span className="req">*</span></label>
            <input placeholder="Vehicle VIN No." value={form.vin} onChange={e => set('vin', e.target.value)} />
          </div>
          {!queryMode && (
            <>
              <div className="fgr">
                <label>Registration No. <span className="req">*</span> <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>(if no registration number then write NA)</span></label>
                <input placeholder="Enter registration number" value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)} />
              </div>
              <div className="fgr">
                <label>Battery ID</label>
                <input placeholder="Battery ID" value={form.batteryId} onChange={e => set('batteryId', e.target.value)} />
              </div>
              <div className="fgr">
                <label>Type of Vehicle <span className="req">*</span></label>
                <select value={form.vehicleType} onChange={e => set('vehicleType', e.target.value)}>
                  <option value="">Select type</option>
                  <option value="E-Auto">E-Auto</option>
                  <option value="E-Loader">E-Loader</option>
                  <option value="E-Rickshaw">E-Rickshaw</option>
                  <option value="2 Wheeler">2 Wheeler</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="fgr">
                <label>OEM Brand Name <span className="req">*</span></label>
                <VehicleFieldDropdown
                  fetchJson={fetchJson}
                  apiUrl="/api/vehicles/oem"
                  value={form.oemBrandName}
                  placeholder="Select brand"
                  onSelect={name => set('oemBrandName', name)}
                  onClear={() => set('oemBrandName', '')}
                />
              </div>
              <div className="fgr">
                <label>Service Provider <span className="req">*</span></label>
                <VehicleFieldDropdown
                  fetchJson={fetchJson}
                  apiUrl="/api/vehicles/service-provider"
                  value={form.serviceProvider}
                  placeholder="Select provider"
                  onSelect={name => set('serviceProvider', name)}
                  onClear={() => set('serviceProvider', '')}
                />
              </div>
              <div className="fgr">
                <label>
                  CNC Error Code <span className="req">*</span>
                  {swapDetailsLoading && <span style={{ marginLeft: 6, fontSize: 10, color: '#0090D9', fontWeight: 500 }}>⏳ Fetching swap details…</span>}
                </label>
                <div ref={cncErrorDropRef} style={{ position: 'relative' }}>
                  <input
                    placeholder="Error code"
                    value={form.cncErrorCode}
                    onChange={e => set('cncErrorCode', e.target.value)}
                    onFocus={() => { if (swapDetailsList.length > 1) setCncErrorDropdownOpen(true) }}
                  />
                  {cncErrorDropdownOpen && (
                    <SwapAttemptDropdown entries={swapDetailsList.slice(1)} primary="error_code" onSelect={applySwapEntry} />
                  )}
                </div>
              </div>
              <div className="fgr">
                <label>
                  CNC Transaction ID <span className="req">*</span>
                  {swapDetailsLoading && <span style={{ marginLeft: 6, fontSize: 10, color: '#0090D9', fontWeight: 500 }}>⏳ Fetching swap details…</span>}
                </label>
                <div ref={cncTxnDropRef} style={{ position: 'relative' }}>
                  <input
                    placeholder="CNC-XXXX"
                    value={form.cncTransactionId}
                    onChange={e => set('cncTransactionId', e.target.value)}
                    onFocus={() => { if (swapDetailsList.length > 1) setCncTxnDropdownOpen(true) }}
                  />
                  {cncTxnDropdownOpen && (
                    <SwapAttemptDropdown entries={swapDetailsList.slice(1)} primary="transaction_id" onSelect={applySwapEntry} />
                  )}
                </div>
                <span style={{ fontSize: 10, color: '#9CA3AF' }}>If no ID available, write NA</span>
              </div>
            </>
          )}
        </div>
      </div>

      {!queryMode && (
        <>
          {/* ══════════ CUSTOMER DETAILS ══════════ */}
          <div className="form-section">
            <div className="fshdr">Customer Details</div>
            <div ref={cDropRef} style={{ position: 'relative', marginBottom: 16 }}>
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Search by name or phone…"
                  value={cSearch}
                  onChange={e => { setCSearch(e.target.value); searchCustomers(e.target.value) }}
                  onKeyDown={e => e.key === 'Enter' && searchCustomers(cSearch)}
                  autoComplete="off"
                />
                <button className="btn btn-primary" onClick={() => searchCustomers(cSearch)} disabled={cSearching}>
                  {cSearching ? '…' : 'Search'}
                </button>
              </div>
              {!cSearching && cResults.length === 0 && cLastSearch && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: '4px', padding: '12px 14px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
                  No results found for &ldquo;{cLastSearch}&rdquo;
                </div>
              )}

              {cResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: '280px', overflowY: 'auto', marginTop: '4px' }}>
                  {cResults.map((r, idx) => {
                    const cd = r.customer_details || r
                    const vd = r.vehicle_details || {}
                    return (
                      <div key={idx} onClick={() => selectCustomerResult(r)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }} onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A1F37' }}>👤 {cd.name || '—'}</div>
                        {cd.phone && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{cd.phone}</div>}
                        {(vd.vin || vd.registration_number) && <div style={{ fontSize: '11px', color: '#9AA5B1', fontFamily: 'monospace', marginTop: '2px' }}>🚗 {[vd.vin, vd.registration_number].filter(Boolean).join(' · ')}</div>}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="fg">
              <div className="fgr">
                <label>Rider Name <span className="req">*</span></label>
                <input placeholder="Full name" value={form.riderName} onChange={e => set('riderName', e.target.value)} />
              </div>
              <div className="fgr">
                <label>Rider Mobile <span className="req">*</span></label>
                <input placeholder="+91 98765 43210" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="fgr">
                <label>Rider Email</label>
                <input type="email" placeholder="rider@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="fgr">
                <label>Rider Preferred Channel</label>
                <select className={form.preferredChannel ? 'selected' : ''} value={form.preferredChannel} onChange={e => set('preferredChannel', e.target.value)}>
                  <option value="">Select channel…</option>
                  {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="fgr">
                <label>Customer Type (Account) <span className="req">*</span></label>
                <select className={form.customerType ? 'selected' : ''} value={form.customerType} onChange={e => set('customerType', e.target.value)}>
                  <option value="">Select type…</option>
                  {CUSTOMER_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="fgr">
                <label>Source Channel <span className="req">*</span></label>
                <select className={form.channel ? 'selected' : ''} value={form.channel} onChange={e => set('channel', e.target.value)}>
                  <option value="">Select source…</option>
                  {SOURCE_CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="fgr">
                <label>Language</label>
                <select className={form.language ? 'selected' : ''} value={form.language} onChange={e => set('language', e.target.value)}>
                  {LANGUAGES.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div className="fgr">
                <label>Vehicle Age <span className="req">*</span></label>
                <select className={form.experience ? 'selected' : ''} value={form.experience} onChange={e => set('experience', e.target.value)}>
                  <option value="">Select vehicle age…</option>
                  {VEHICLE_AGES.map(ex => <option key={ex.value} value={ex.value}>{ex.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      {issueBlock}
    </div>
  )
}

export default CreateTicketPage
